import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useOrganizationInvitations } from '@/hooks/useOrganizationInvitations';
import { useOrganizationRoles } from '@/hooks/useOrganizationRoles';
import { usePermissions } from '@/hooks/usePermissions';
import { OrganizationInviteDialog } from './OrganizationInviteDialog';
import { OrgRoleBadge } from './OrgRoleBadge';
import { supabase } from '@/integrations/supabase/client';
import { Users, Mail, Clock, CheckCircle, XCircle, Trash2, Info, ChevronDown } from 'lucide-react';
import { useAuth } from '@/lib/auth';

interface OrganizationMembersListProps {
  organizationId: string;
}

interface Member {
  user_id: string;
  email: string;
  role: string;
  org_role?: 'owner' | 'super_user' | 'member';
  first_name?: string;
  last_name?: string;
}

export function OrganizationMembersList({ organizationId }: OrganizationMembersListProps) {
  const { user } = useAuth();
  const { canManageMembers } = usePermissions();
  const [members, setMembers] = useState<Member[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const { invitations, loading: loadingInvitations, cancelInvitation } = useOrganizationInvitations(organizationId);
  const { roles, updateRole, isUpdating } = useOrganizationRoles(organizationId);

  // Guard against invalid organization ID
  if (!organizationId || organizationId === "placeholder-org-id") {
    return (
      <div className="text-muted-foreground">
        Unable to load organization data. Please refresh the page or contact support.
      </div>
    );
  }

  useEffect(() => {
    fetchMembers();
  }, [organizationId, roles]);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      
      // Get emails using security definer function
      const { data: memberEmails, error: emailError } = await supabase
        .rpc('get_organization_member_emails', {
          _org_id: organizationId
        });
      
      if (emailError) throw emailError;
      
      // Get profiles for this organization
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('user_id, role')
        .eq('organization_id', organizationId);

      if (error) throw error;

      const membersWithDetails = await Promise.all(
        (profiles || []).map(async (profile) => {
          // Get email from RPC result
          const emailData = memberEmails?.find(e => e.user_id === profile.user_id);
          const email = emailData?.email || 
                       (profile.user_id === user?.id ? user.email : 'Unknown');
          
          // Try to get client profile for name
          const { data: clientProfile } = await supabase
            .from('client_profiles')
            .select('first_name, last_name')
            .eq('user_id', profile.user_id)
            .maybeSingle();

          // Try to get vendor profile for name if client not found
          let firstName = clientProfile?.first_name;
          let lastName = clientProfile?.last_name;
          
          if (!firstName && !lastName) {
            const { data: vendorProfile } = await supabase
              .from('vendor_profiles_enhanced')
              .select('business_name')
              .eq('user_id', profile.user_id)
              .maybeSingle();
            
            if (vendorProfile?.business_name) {
              firstName = vendorProfile.business_name;
            }
          }

          // Get org role
          const orgRole = roles?.find(r => r.user_id === profile.user_id)?.org_role;

          return {
            user_id: profile.user_id,
            email,
            role: profile.role,
            org_role: orgRole,
            first_name: firstName,
            last_name: lastName,
          };
        })
      );

      setMembers(membersWithDetails);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <Badge variant="secondary">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
      case 'accepted':
        return (
          <Badge variant="default" className="bg-success">
            <CheckCircle className="h-3 w-3 mr-1" />
            Accepted
          </Badge>
        );
      case 'expired':
      case 'rejected':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getInitials = (firstName?: string, lastName?: string, email?: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="space-y-6">
      {/* Current Members */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5" />
            Current Members
          </h3>
          <p className="text-sm text-muted-foreground">
            Manage roles and permissions
          </p>
        </div>
        {canManageMembers && <OrganizationInviteDialog organizationId={organizationId} />}
      </div>

      {/* Role Descriptions */}
      <Collapsible className="mb-4">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground p-0 h-auto">
            <Info className="h-4 w-4" />
            <span className="text-sm">What can each role do?</span>
            <ChevronDown className="h-4 w-4 transition-transform duration-200 [[data-state=open]>svg:last-child]:rotate-180" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <div className="bg-muted/50 rounded-lg p-4 space-y-3 text-sm">
            <div>
              <span className="font-semibold text-foreground">Owner</span>
              <span className="text-muted-foreground"> — Full administrative control. Can manage organization settings, team members, billing, and all jobs/bids across the organization.</span>
            </div>
            <div>
              <span className="font-semibold text-foreground">Super User</span>
              <span className="text-muted-foreground"> — Procurement power user. Can manage payments and view/edit all jobs and bids, but cannot change organization settings or manage team members.</span>
            </div>
            <div>
              <span className="font-semibold text-foreground">Member</span>
              <span className="text-muted-foreground"> — Personal workspace. Can create and manage only their own jobs and bids. Cannot access other members' work or organization settings.</span>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {loadingMembers ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse flex items-center gap-3 p-3 border rounded">
                  <div className="h-10 w-10 bg-muted rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : members.length > 0 ? (
            <div className="space-y-2">
              {members.map((member) => (
                <div key={member.user_id} className="flex items-center gap-3 p-3 border rounded hover:bg-secondary/50 transition-colors">
                  <Avatar>
                    <AvatarFallback>
                      {getInitials(member.first_name, member.last_name, member.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-medium">
                      {member.first_name && member.last_name
                        ? `${member.first_name} ${member.last_name}`
                        : member.email}
                    </p>
                    <p className="text-sm text-muted-foreground">{member.email}</p>
                  </div>
                  
                  {/* Organization Role Badge or Selector */}
                  {canManageMembers && member.user_id !== user?.id ? (
                    <Select 
                      value={member.org_role || 'member'} 
                      onValueChange={(role: 'owner' | 'super_user' | 'member') => updateRole({ userId: member.user_id, newRole: role })}
                      disabled={isUpdating}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="super_user">Super User</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    member.org_role && <OrgRoleBadge role={member.org_role} />
                  )}
                  
                  <Badge variant="outline" className="capitalize">
                    {member.role}
                  </Badge>
                  {member.user_id === user?.id && (
                    <Badge variant="secondary">You</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No members found
            </p>
          )}

      {/* Pending Invitations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Pending Invitations
          </CardTitle>
          <CardDescription>
            Invitations that haven't been accepted yet
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loadingInvitations ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="animate-pulse flex items-center gap-3 p-3 border rounded">
                  <div className="flex-1">
                    <div className="h-4 bg-muted rounded w-1/3 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-1/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : invitations.filter(inv => inv.status === 'pending').length > 0 ? (
            <div className="space-y-2">
              {invitations
                .filter(inv => inv.status === 'pending')
                .map((invitation) => (
                  <div key={invitation.id} className="flex items-center gap-3 p-3 border rounded">
                    <Mail className="h-8 w-8 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="font-medium">{invitation.email}</p>
                      <p className="text-sm text-muted-foreground">
                        Invited {new Date(invitation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                      <Badge variant="outline" className="capitalize">
                        {invitation.org_role === 'super_user' ? 'Super User' : 'Member'}
                      </Badge>
                    {getStatusBadge(invitation.status)}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelInvitation(invitation.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No pending invitations
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
